import React, { useState } from "react";
import { Loader } from "lucide-react";
import ConversionLogFilters from "./ConversionLogFilters";

const formatAmount = (value) => {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  if (isNaN(num)) return "—";
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 });
};

const formatRate = (value) => (value === null || value === undefined ? "N/A" : formatAmount(value));

// Shared by the Merchant and Consumer tabs - plain "what was converted,
// when, at what rate (and now, which provider priced it)" visibility,
// every conversion regardless of whether it ever settled at a partner.
// Distinct from SettlementLinkedTable's payout-linked/P&L focus - see the
// treasury implementation plan's own reasoning for keeping these separate.
function ConversionHistoryTable({ useHistory, subjectLabel, subjectAccessor, emptyMessage }) {
  const [currency, setCurrency] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data, isLoading, isError, error } = useHistory({
    currency: currency.trim().toUpperCase(),
    from,
    to,
  });

  if (isError) {
    return (
      <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
        Error: {error.message}
      </p>
    );
  }

  const rows = data ?? [];

  return (
    <div>
      <p className="mb-4 text-sm text-gray-500">
        Most recent 200 conversions - support/visibility only, not tied to partner settlement.
      </p>
      <ConversionLogFilters
        currency={currency}
        onCurrencyChange={setCurrency}
        from={from}
        onFromChange={setFrom}
        to={to}
        onToChange={setTo}
      />

      {isLoading ? (
        <div className="flex h-[300px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading conversion history <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Date</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">{subjectLabel}</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Currency</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Amount</th>
                  <th title="The provider's base rate, as stored, before our spread" className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Provider Rate</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Novacrust Rate</th>
                  <th title="What the spread earned, in the non-USD currency of the pair" className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Spread</th>
                  <th title="Platform fee, in the source currency" className="px-4 py-4 text-xs font-bold tracking-wider text-right text-gray-500 uppercase border-b md:py-4 md:px-6">Platform Fee</th>
                  <th className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6">Provider</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length > 0 ? (
                  rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-gray-50">
                      <td className="px-4 py-3 font-sans text-sm text-gray-600 whitespace-nowrap md:px-6 md:py-5">
                        {row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-gray-900 md:px-6 md:py-5">
                        {subjectAccessor(row) ?? "—"}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-gray-600 uppercase md:px-6 md:py-5">
                        {row.sourceCurrency} → {row.destinationCurrency}
                        {row.destinationCurrencyWasShort && (
                          <span
                            className="block mt-0.5 text-xs font-medium normal-case text-amber-700"
                            title={`${row.destinationCurrency?.toUpperCase()} was already running a merchant↔partner shortfall on this day. The conversion was allowed; this is a flag only.`}
                          >
                            into a short currency
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                        {formatAmount(row.sourceAmount)} → {formatAmount(row.destinationAmount)}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                        {formatRate(row.providerExchangeRate)}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                        {formatRate(row.novacrustExchangeRate)}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                        {row.transactionSpread === null || row.transactionSpread === undefined ? "N/A" : `${formatAmount(row.transactionSpread)} ${(row.spreadCurrency || "").toUpperCase()}`}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-right text-gray-900 md:px-6 md:py-5">
                        {row.platformFee === null || row.platformFee === undefined ? "N/A" : `${formatAmount(row.platformFee)} ${(row.platformFeeCurrency || "").toUpperCase()}`}
                        {row.earningsFromConsumerTrade && (
                          <span
                            className="block text-xs text-gray-400"
                            title="The house merchant converts at the base rate, so the spread and fee shown here are the ones on the consumer's own conversion."
                          >
                            from consumer trade
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-sans text-sm text-gray-600 capitalize md:px-6 md:py-5">
                        {row.rateProvider?.toLowerCase() ?? "N/A"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                      {emptyMessage}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConversionHistoryTable;
